import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as CompanyIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { usePageTitle } from 'contexts/PageTitleContext';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import FlexBetween from 'components/flexbox/FlexBetween';

const CompanyCategoryManagement = () => {
  const theme = useTheme();
  const { setPageTitle } = usePageTitle();

  // Check if user is Company type
  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();
  const companyDetails = apiService.getCompanyDetails();

  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(isCompanyUser ? companyId : '');

  const [companyDialog, setCompanyDialog] = useState({ 
    open: false, 
    mode: 'add', 
    id: null, 
    name: '',
    logoFile: null,
    logoPreview: null,
    address: '',
    mobile_number: '',
    gst_number: '',
    registration_number: '',
    certification_number: ''
  });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'add', id: null, name: '' });
  const [companyUserDialog, setCompanyUserDialog] = useState({ 
    open: false, 
    mode: 'add', // 'add' or 'edit'
    user_id: null,
    first_name: '', 
    mobile_number: '', 
    email: '',
    company_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
const [errors, setErrors] = useState({});
const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  // Delete confirmation dialogs
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, id: null, name: '' });

  useEffect(() => {
    if (isCompanyUser) {
      setPageTitle('Category Management');
    } else {
      setPageTitle('Company & Category Management');
    }
  }, [setPageTitle, isCompanyUser]);

  const userId = useMemo(() => localStorage.getItem('user_id') || '', []);

  const fetchCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const res = await apiService.get(API_ENDPOINTS.GET_company);
      if (res?.success) {
        setCompanies(res.data?.Company || []);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const fetchCategories = useCallback(async (companyId) => {
    if (!companyId) {
      setCategories([]);
      return;
    }
    try {
      setCategoriesLoading(true);
      const res = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${companyId}&user_id=${userId}`);
      if (res?.success) {
        setCategories(res.data?.ProductCategory || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  }, [userId]);

  const fetchCompanyUsers = useCallback(async (companyId) => {
    if (!companyId) {
      setCompanyUsers([]);
      return;
    }
    try {
      setCompanyUsersLoading(true);
      const res = await apiService.get(`${API_ENDPOINTS.GET_company_related_users}?company_id=${companyId}`);
      if (res?.success) {
        setCompanyUsers(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching company users:', err);
    } finally {
      setCompanyUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch companies if user is Admin
    if (!isCompanyUser) {
      fetchCompanies();
    }
  }, [isCompanyUser]);

  useEffect(() => {
    fetchCategories(selectedCompanyId);
    fetchCompanyUsers(selectedCompanyId);
  }, [selectedCompanyId]);

  // Company Actions
  const openAddCompany = () => setCompanyDialog({ 
    open: true, 
    mode: 'add', 
    id: null, 
    name: '',
    logoFile: null,
    logoPreview: null,
    address: '',
    mobile_number: '',
    gst_number: '',
    registration_number: '',
    certification_number: ''
  });
const validateCompanyForm = () => {
  const newErrors = {};

  if (!companyDialog.name.trim()) newErrors.name = "Company name is required";
  if (!companyDialog.address.trim()) newErrors.address = "Address is required";
  if (!companyDialog.mobile_number.trim()) {
    newErrors.mobile_number = "Mobile number is required";
  } else if (!/^\d{10}$/.test(companyDialog.mobile_number)) {
    newErrors.mobile_number = "Enter a valid 10-digit number";
  }
  if (!companyDialog.gst_number.trim()) newErrors.gst_number = "GST number is required";
  if (!companyDialog.registration_number.trim()) newErrors.registration_number = "Registration number is required";
  if (!companyDialog.certification_number.trim()) newErrors.certification_number = "Certification number is required";
  // Logo is optional - no validation required

  setErrors(newErrors);

  if (Object.keys(newErrors).length > 0) {
    setSnackbar({ open: true, message: "Please fill all required fields." });
    return false;
  }
  return true;
};



  const openEditCompany = (company) => setCompanyDialog({ 
    open: true, 
    mode: 'edit', 
    id: company.id, 
    name: company.name,
    logoFile: null,
    logoPreview: company.logo,
    address: company.address || '',
    mobile_number: company.mobile_number || '',
    gst_number: company.gst_number || '',
    registration_number: company.registration_number || '',
    certification_number: company.certification_number || ''
  });
  
  const closeCompanyDialog = () => setCompanyDialog({ 
    open: false, 
    mode: 'add', 
    id: null, 
    name: '',
    logoFile: null, 
    logoPreview: null,
    address: '',
    mobile_number: '',
    gst_number: '',
    registration_number: '',
    certification_number: ''
  });

const submitCompany = async () => {
  if (!validateCompanyForm()) return; // stop if not valid

  try {
    setSubmitting(true);
    const fd = new FormData();

    fd.append("name", companyDialog.name);
    fd.append("address", companyDialog.address);
    fd.append("mobile_number", companyDialog.mobile_number);
    fd.append("gst_number", companyDialog.gst_number);
    fd.append("registration_number", companyDialog.registration_number);
    fd.append("certification_number", companyDialog.certification_number);
    fd.append("user_id", userId);

    if (companyDialog.logoFile) fd.append("logo_image", companyDialog.logoFile);

    let res;
    if (companyDialog.mode === "add") {
      res = await apiService.post(API_ENDPOINTS.POST_add_company, fd, "multipart/form-data");
    } else {
      fd.append("id", companyDialog.id);
      res = await apiService.put(API_ENDPOINTS.UPDATE_company, fd, "multipart/form-data");
    }

    if (res?.success) {
      closeCompanyDialog();
      fetchCompanies();
    }
  } catch (err) {
    console.error("Company submit error:", err);
  } finally {
    setSubmitting(false);
  }
};




  const confirmDeleteCompany = (company) => {
    setDeleteConfirm({
      open: true,
      type: 'company',
      id: company.id,
      name: company.name,
    });
  };

  const deleteCompany = async () => {
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('id', deleteConfirm.id);
      fd.append('user_id', userId);
      const res = await apiService.delete(`${API_ENDPOINTS.DELETE_company}?company_id=${deleteConfirm.id}&user_id=${userId}`,  'multipart/form-data');
      if (res?.success) {
        if (selectedCompanyId === deleteConfirm.id) setSelectedCompanyId('');
        fetchCompanies();
        setCategories([]);
        setCompanyUsers([]);
      }
    } catch (err) {
      console.error('Delete company error:', err);
    } finally {
      setSubmitting(false);
      setDeleteConfirm({ open: false, type: null, id: null, name: '' });
    }
  };

  // Category Actions
  const openAddCategory = () => setCategoryDialog({ open: true, mode: 'add', id: null, name: '' });
  const openEditCategory = (category) => setCategoryDialog({ open: true, mode: 'edit', id: category.id, name: category.name });
  const closeCategoryDialog = () => setCategoryDialog({ open: false, mode: 'add', id: null, name: '' });

  const submitCategory = async () => {
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('name', categoryDialog.name || '');
      if (!selectedCompanyId) {
        alert('Please select a company first');
        return;
      }
      fd.append('company', selectedCompanyId);
      fd.append('user_id', userId);
      let res;
      if (categoryDialog.mode === 'add') {
        res = await apiService.post(API_ENDPOINTS.POST_add_product_category, fd, 'multipart/form-data');
      } else {
        fd.append('id', categoryDialog.id);
        res = await apiService.put(API_ENDPOINTS.UPDATE_product_category, fd,'multipart/form-data');
      }
      if (res?.success) {
        closeCategoryDialog();
        fetchCategories(selectedCompanyId);
      }
    } catch (err) {
      console.error('Category submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteCategory = (category) => {
    setDeleteConfirm({
      open: true,
      type: 'category',
      id: category.id,
      name: category.name,
    });
  };

  const deleteCategory = async () => {
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('id', deleteConfirm.id);
      fd.append('user_id', userId);
      const res = await apiService.delete(`${API_ENDPOINTS.DELETE_product_category}?product_category_id=${deleteConfirm.id}&user_id=${userId}`, 'multipart/form-data');
      if (res?.success) {
        fetchCategories(selectedCompanyId);
      }
    } catch (err) {
      console.error('Delete category error:', err);
    } finally {
      setSubmitting(false);
      setDeleteConfirm({ open: false, type: null, id: null, name: '' });
    }
  };

  const selectedCompanyName = isCompanyUser 
    ? companyDetails?.name || '' 
    : companies.find(c => c.id === selectedCompanyId)?.name || '';

  // Validation function
  const validateForm = () => {
    const errors = {};
    
    if (!companyUserDialog.first_name.trim()) {
      errors.first_name = 'Company name is required';
    }
    
    if (!companyUserDialog.mobile_number.trim()) {
      errors.mobile_number = 'Mobile number is required';
    } else if (!/^[6-9]\d{9}$/.test(companyUserDialog.mobile_number.replace(/\D/g, ''))) {
      errors.mobile_number = 'Please enter a valid 10-digit mobile number';
    }
    
    if (!companyUserDialog.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyUserDialog.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!companyUserDialog.company_id) {
      errors.company_id = 'Company selection is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Company User Actions
  const openAddCompanyUser = () => {
    // Get the selected company's mobile number
    const selectedCompany = companies.find(c => c.id === selectedCompanyId);
    const companyMobileNumber = selectedCompany?.mobile_number || '';
    
    setCompanyUserDialog({ 
      open: true, 
      mode: 'add',
      user_id: null,
      first_name: selectedCompanyName, 
      mobile_number: companyMobileNumber, 
      email: '',
      company_id: selectedCompanyId
    });
    setFormErrors({});
  };

  const openEditCompanyUser = (userRelation) => {
    setCompanyUserDialog({ 
      open: true, 
      mode: 'edit',
      user_id: userRelation.user?.id,
      first_name: userRelation.user?.first_name || '', 
      mobile_number: userRelation.user?.mobile_number || '', 
      email: userRelation.user?.email || '',
      company_id: selectedCompanyId
    });
    setFormErrors({});
  };

  const closeCompanyUserDialog = () => {
    setCompanyUserDialog({ 
      open: false, 
      mode: 'add',
      user_id: null,
      first_name: '', 
      mobile_number: '', 
      email: '',
      company_id: ''
    });
    setFormErrors({});
  };

  const submitCompanyUser = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('first_name', companyUserDialog.first_name.trim());
      fd.append('mobile_number', companyUserDialog.mobile_number.replace(/\D/g, ''));
      fd.append('email', companyUserDialog.email.trim());
      fd.append('company_id', companyUserDialog.company_id);
      fd.append('user_id', userId);

      let res;
      if (companyUserDialog.mode === 'add') {
        res = await apiService.post(
          API_ENDPOINTS.POST_create_company_login,
          fd,
          'multipart/form-data'
        );
      } else {
        // Update existing user
        fd.append('company_user_id', companyUserDialog.user_id);
        res = await apiService.put(
          API_ENDPOINTS.PUT_update_company_login,
          fd,
          'multipart/form-data'
        );
      }

      if (res?.success) {
        closeCompanyUserDialog();
        fetchCompanyUsers(selectedCompanyId);
        const action = companyUserDialog.mode === 'add' ? 'created' : 'updated';
        alert(`Company user ${action} successfully!`);
      } else {
        const action = companyUserDialog.mode === 'add' ? 'create' : 'update';
        alert(res?.message || `Failed to ${action} company user`);
      }
    } catch (err) {
      console.error('Company user operation error:', err);
      const action = companyUserDialog.mode === 'add' ? 'create' : 'update';
      alert(`Failed to ${action} company user. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
      p: { xs: 2, sm: 3, md: 4 }
    }}>
   

      {/* Main Content */}
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Companies Section - Only show for Admin users */}
        {!isCompanyUser && (
        <Box sx={{ mb: 4 }}>
          <Card sx={{ 
            borderRadius: 4,
            boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.1)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              p: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
              color: 'white'
            }}>
              <FlexBetween>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: alpha('#fff', 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CompanyIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      Companies
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Manage your business entities
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Refresh companies">
                    <IconButton 
                      onClick={fetchCompanies} 
                      sx={{ 
                        color: 'white',
                        bgcolor: alpha('#fff', 0.1),
                        '&:hover': { bgcolor: alpha('#fff', 0.2) }
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={openAddCompany} 
                    sx={{ 
                      textTransform: 'none',
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      px: 3,
                      '&:hover': { 
                        bgcolor: alpha('#fff', 0.9),
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Add Company
                  </Button>
                </Box>
              </FlexBetween>
            </Box>

            <Box sx={{ p: 3 }}>
              {companiesLoading ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress size={40} />
                </Box>
              ) : companies.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No companies found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start by adding your first company
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={{ xs: 1, sm: 2, md: 2 }}>
                  {companies.map((c) => (
                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={c.id}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          border: selectedCompanyId === c.id ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          '&:hover': { 
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                          }
                        }}
                        onClick={() => setSelectedCompanyId(c.id)}
                      >
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                          {/* Top Section: Logo + Name + ID */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 50,
                                height: 50,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                flexShrink: 0
                              }}
                            >
                              <Box
                                component="img"
                                src={c.logo}
                                alt={c.name}
                                sx={{
                                  width: "80%",
                                  height: "80%",
                                  objectFit: "contain",
                                }}
                              />
                            </Avatar>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ 
                                fontSize: '0.875rem',
                                lineHeight: 1.2,
                                wordBreak: 'break-word',
                                mb: 0.25
                              }}>
                                {c.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                ID: {c.id}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Middle Section: Key Details in Compact Format */}
                          <Box sx={{ flexGrow: 1, mb: 1.5, minHeight: 120 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                              {c.mobile_number && (
                                <Tooltip title={`Mobile: ${c.mobile_number}`} arrow>
                                  <Chip 
                                    size="small" 
                                    label={`📱 ${c.mobile_number}`}
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      height: 20,
                                      maxWidth: '100%',
                                      '& .MuiChip-label': { 
                                        px: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {c.gst_number && (
                                <Tooltip title={`GST: ${c.gst_number}`} arrow>
                                  <Chip 
                                    size="small" 
                                    label={`🏢 ${c.gst_number}`}
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      height: 20,
                                      maxWidth: '100%',
                                      '& .MuiChip-label': { 
                                        px: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {c.registration_number && (
                                <Tooltip title={`Registration: ${c.registration_number}`} arrow>
                                  <Chip 
                                    size="small" 
                                    label={`📋 ${c.registration_number}`}
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      height: 20,
                                      maxWidth: '100%',
                                      '& .MuiChip-label': { 
                                        px: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {c.certification_number && (
                                <Tooltip title={`Certification: ${c.certification_number}`} arrow>
                                  <Chip 
                                    size="small" 
                                    label={`🏆 ${c.certification_number}`}
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      height: 20,
                                      maxWidth: '100%',
                                      '& .MuiChip-label': { 
                                        px: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>

                            {c.address && (
                              <Box sx={{ mt: 1 }}>
                                <Tooltip title={`Address: ${c.address}`} arrow>
                                  <Typography variant="caption" sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    fontSize: '0.7rem',
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer'
                                  }}>
                                    📍 {c.address}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>

                          {/* Bottom Section: Action Buttons */}
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            justifyContent: 'flex-end',
                            pt: 1, 
                            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.3)}` 
                          }}>
                            <Tooltip title="Edit company">
                              <IconButton 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditCompany(c);
                                }}
                                sx={{ 
                                  width: 24,
                                  height: 24,
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                                }}
                              >
                                <EditIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete company">
                              <IconButton 
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDeleteCompany(c);
                                }}
                                sx={{ 
                                  width: 24,
                                  height: 24,
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Card>
        </Box>
        )}

        {/* Categories and Users Section */}
        {selectedCompanyId && (
          <Grid container spacing={3}>
            {/* Categories */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                borderRadius: 4,
                boxShadow: `0 20px 60px ${alpha(theme.palette.secondary.main, 0.1)}`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                height: '100%'
              }}>
                <Box sx={{ 
                  p: 3,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${alpha(theme.palette.secondary.main, 0.8)})`,
                  color: 'white'
                }}>
                  <FlexBetween>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        bgcolor: alpha('#fff', 0.2),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CategoryIcon sx={{ fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={700}>
                          Categories
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {selectedCompanyName}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openAddCategory}
                      sx={{ 
                        textTransform: 'none',
                        bgcolor: 'white',
                        color: theme.palette.secondary.main,
                        fontWeight: 600,
                        px: 3,
                        '&:hover': { 
                          bgcolor: alpha('#fff', 0.9),
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Add Category
                    </Button>
                  </FlexBetween>
                </Box>

                <Box sx={{ p: 3, flexGrow: 1 }}>
                  {categoriesLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : categories.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No categories found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add categories for this company
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {categories.map((cat) => (
                        <Card key={cat.id} sx={{ 
                          p: 2,
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                          '&:hover': { 
                            boxShadow: `0 8px 25px ${alpha(theme.palette.secondary.main, 0.1)}`
                          }
                        }}>
                          <FlexBetween>
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {cat.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ID: {cat.id}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Edit category">
                                <IconButton 
                                  size="small"
                                  onClick={() => openEditCategory(cat)}
                                  sx={{ 
                                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                    '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.2) }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete category">
                                <IconButton 
                                  size="small"
                                  color="error"
                                  onClick={() => confirmDeleteCategory(cat)}
                                  sx={{ 
                                    bgcolor: alpha(theme.palette.error.main, 0.1),
                                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </FlexBetween>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>

            {/* Company Users */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                borderRadius: 4,
                boxShadow: `0 20px 60px ${alpha(theme.palette.success.main, 0.1)}`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                height: '100%'
              }}>
                <Box sx={{ 
                  p: 3,
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`,
                  color: 'white'
                }}>
                  <FlexBetween>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        bgcolor: alpha('#fff', 0.2),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <PeopleIcon sx={{ fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={700}>
                          Company User
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {selectedCompanyName}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Refresh users">
                        <IconButton 
                          onClick={() => fetchCompanyUsers(selectedCompanyId)}
                          sx={{ 
                            color: 'white',
                            bgcolor: alpha('#fff', 0.1),
                            '&:hover': { bgcolor: alpha('#fff', 0.2) }
                          }}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      {/* Only show Create User button if no users exist */}
                      {companyUsers.length === 0 && (
                        <Button 
                          variant="contained" 
                          startIcon={<AddIcon />} 
                          onClick={openAddCompanyUser} 
                          sx={{ 
                            textTransform: 'none',
                            bgcolor: 'white',
                            color: theme.palette.success.main,
                            fontWeight: 600,
                            px: 3,
                            '&:hover': { 
                              bgcolor: alpha('#fff', 0.9),
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Create User
                        </Button>
                      )}
                    </Box>
                  </FlexBetween>
                </Box>

                <Box sx={{ p: 3, flexGrow: 1 }}>

                  {companyUsersLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : companyUsers.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No users found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No users associated with this company
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {companyUsers.map((userRelation) => (
                        <Card key={userRelation.id} sx={{ 
                          p: 3,
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                          '&:hover': { 
                            boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.1)}`
                          }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Avatar sx={{ 
                              width: 60, 
                              height: 60, 
                              bgcolor: theme.palette.success.main,
                              fontSize: '1.5rem',
                              fontWeight: 700
                            }}>
                              {userRelation.user?.first_name?.charAt(0) || 'U'}
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                                {userRelation.user?.first_name || ''} {userRelation.user?.last_name || ''}
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  📱 {userRelation.user?.mobile_number || 'N/A'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ✉️ {userRelation.user?.email || 'N/A'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                  <Chip 
                                    label={userRelation.user?.status || 'N/A'} 
                                    size="small" 
                                    color={userRelation.user?.status === 'Active' ? 'success' : 'default'}
                                    sx={{ fontWeight: 600 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Added: {new Date(userRelation.created_at).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Edit user">
                                <IconButton 
                                  size="small"
                                  onClick={() => openEditCompanyUser(userRelation)}
                                  sx={{ 
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* No Company Selected Message - Only show for Admin users */}
        {!selectedCompanyId && !isCompanyUser && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.5)}, ${alpha(theme.palette.grey[50], 0.5)})`,
            borderRadius: 4,
            border: `2px dashed ${alpha(theme.palette.grey[300], 0.5)}`
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: '50%', 
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}>
              <CompanyIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
            </Box>
            <Typography variant="h5" fontWeight={600} color="text.secondary" sx={{ mb: 2 }}>
              Select a Company
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Choose a company from the list above to view and manage its categories and users
            </Typography>
          </Box>
        )}
      </Box>

      {/* Company Dialog */}
      <Dialog open={companyDialog.open} onClose={closeCompanyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {companyDialog.mode === 'add' ? 'Add Company' : 'Edit Company'}
        </DialogTitle>

     

  <DialogContent>
<TextField
  fullWidth
  label="Company Name"
  value={companyDialog.name}
  onChange={(e) => {
    const value = e.target.value;
    setCompanyDialog((prev) => ({ ...prev, name: value }));

    // clear error when user fixes the field
    if (errors.name && value.trim()) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  }}
  error={!!errors.name}
  helperText={errors.name}
  sx={{ mt: 1 }}
/>

<TextField
  fullWidth
  label="Address"
  multiline
  rows={3}
  value={companyDialog.address}
  onChange={(e) => {
    const value = e.target.value;
    setCompanyDialog((prev) => ({ ...prev, address: value }));

    if (errors.address && value.trim()) {
      setErrors((prev) => ({ ...prev, address: undefined }));
    }
  }}
  error={!!errors.address}
  helperText={errors.address}
  sx={{ mt: 3 }}
/>

<TextField
  fullWidth
  label="Mobile Number"
  value={companyDialog.mobile_number}
  onChange={(e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setCompanyDialog((prev) => ({ ...prev, mobile_number: value }));

    // clear error when valid 10-digit number entered
    if (errors.mobile_number && /^\d{10}$/.test(value)) {
      setErrors((prev) => ({ ...prev, mobile_number: undefined }));
    }
  }}
  placeholder="Enter 10-digit mobile number"
  error={!!errors.mobile_number}
  helperText={errors.mobile_number}
  sx={{ mt: 3 }}
/>



   <TextField
  fullWidth
  label="GST Number"
  value={companyDialog.gst_number}
  onChange={(e) => {
    const value = e.target.value;
    setCompanyDialog((prev) => ({ ...prev, gst_number: value }));
    if (errors.gst_number && value.trim()) {
      setErrors((prev) => ({ ...prev, gst_number: undefined }));
    }
  }}
  error={!!errors.gst_number}
  helperText={errors.gst_number}
  sx={{ mt: 3 }}
/>

<TextField
  fullWidth
  label="Registration Number"
  value={companyDialog.registration_number}
  onChange={(e) => {
    const value = e.target.value;
    setCompanyDialog((prev) => ({ ...prev, registration_number: value }));
    if (errors.registration_number && value.trim()) {
      setErrors((prev) => ({ ...prev, registration_number: undefined }));
    }
  }}
  error={!!errors.registration_number}
  helperText={errors.registration_number}
  sx={{ mt: 3 }}
/>

<TextField
  fullWidth
  label="Certification Number"
  value={companyDialog.certification_number}
  onChange={(e) => {
    const value = e.target.value;
    setCompanyDialog((prev) => ({ ...prev, certification_number: value }));
    if (errors.certification_number && value.trim()) {
      setErrors((prev) => ({ ...prev, certification_number: undefined }));
    }
  }}
  error={!!errors.certification_number}
  helperText={errors.certification_number}
  sx={{ mt: 3 }}
/>


<TextField
  fullWidth
  label="Company Logo"
  value={companyDialog.logoFile ? "Logo selected" : ""}
  InputProps={{
    readOnly: true,
    endAdornment: (
      <Button component="label" variant="outlined" size="small">
        Browse
        <input
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const previewUrl = URL.createObjectURL(file);
              setCompanyDialog((prev) => ({
                ...prev,
                logoFile: file,
                logoPreview: previewUrl,
              }));

              // ✅ clear error when file selected
              if (errors.logoFile) {
                setErrors((prev) => ({ ...prev, logoFile: undefined }));
              }
            }
          }}
        />
      </Button>
    ),
  }}
  error={!!errors.logoFile}
  helperText={errors.logoFile}
  sx={{ mt: 3 }}
/>


{/* Preview Button */}
{companyDialog.logoPreview && (
  <Box mt={2}>
    <Button
      variant="outlined"
      onClick={() => setCompanyDialog((prev) => ({ ...prev, showPreview: true }))}
    >
      Preview Logo
    </Button>

    <Dialog
      open={companyDialog.showPreview || false}
      onClose={() => setCompanyDialog((prev) => ({ ...prev, showPreview: false }))}
    >
      <DialogTitle>Logo Preview</DialogTitle>
      <DialogContent sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <Avatar
          src={companyDialog.logoPreview}
          alt="Logo Preview"
          sx={{ width: 120, height: 120 }}
        />
      </DialogContent>
    </Dialog>
  </Box>
)}

  </DialogContent>


        <DialogActions>
          <Button onClick={closeCompanyDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submitCompany}
            variant="contained"
            disabled={submitting || !companyDialog.name.trim()}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {companyDialog.mode === "add" ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Category Dialog */}
      <Dialog open={categoryDialog.open} onClose={closeCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{categoryDialog.mode === 'add' ? 'Add Category' : 'Edit Category'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Category Name"
            value={categoryDialog.name}
            onChange={(e) => setCategoryDialog(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCategoryDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={submitCategory} variant="contained" disabled={submitting || !categoryDialog.name.trim() || !selectedCompanyId} startIcon={submitting ? <CircularProgress size={16} /> : null}>
            {categoryDialog.mode === 'add' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Irreversible Delete Confirmation */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, type: null, id: null, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Permanent Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {deleteConfirm.type === 'company' ? (
              <>You are about to permanently delete the company <strong>{deleteConfirm.name}</strong>. All related categories and products will also be deleted and cannot be recovered.</>
            ) : (
              <>You are about to permanently delete the category <strong>{deleteConfirm.name}</strong>. All related products will also be deleted and cannot be recovered.</>
            )}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 600 }}>
            This action is irreversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, type: null, id: null, name: '' })} disabled={submitting}>Cancel</Button>
          <Button
            onClick={deleteConfirm.type === 'company' ? deleteCompany : deleteCategory}
            color="error"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Company User Dialog */}
      <Dialog open={companyUserDialog.open} onClose={closeCompanyUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {companyUserDialog.mode === 'add' ? 'Create Company User' : 'Edit Company User'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Company Name"
            value={companyUserDialog.first_name}
            onChange={(e) =>
              setCompanyUserDialog((prev) => ({ ...prev, first_name: e.target.value }))
            }
            error={!!formErrors.first_name}
            helperText={formErrors.first_name}
            sx={{ mt: 1 }}
          />
          
          <TextField
            fullWidth
            label="Mobile Number"
            value={companyUserDialog.mobile_number}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
              setCompanyUserDialog((prev) => ({ ...prev, mobile_number: value }));
            }}
            error={!!formErrors.mobile_number}
            helperText={formErrors.mobile_number}
            placeholder="Enter 10-digit mobile number"
            sx={{ mt: 3 }}
          />
          
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={companyUserDialog.email}
            onChange={(e) =>
              setCompanyUserDialog((prev) => ({ ...prev, email: e.target.value }))
            }
            error={!!formErrors.email}
            helperText={formErrors.email}
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCompanyUserDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submitCompanyUser}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {companyUserDialog.mode === 'add' ? 'Create User' : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyCategoryManagement; 